import { useState } from "react";
import { get } from "../lib/api.js";
import { fmtDate, money, todayIso } from "../lib/format.js";
import { useBranch } from "../lib/session.js";
import { useQuery } from "../lib/useQuery.js";
import { Card, Empty, Field, Loading, Money, PageHead, Segmented } from "../components/ui.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

function monthStart(): string {
  return `${todayIso().slice(0, 7)}-01`;
}

/** Referrals per doctor in a period: orders, studies and revenue. Informational, no commissions (meeting 2026-10-31). */
export function DoctorReportPage() {
  const branch = useBranch();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [scope, setScope] = useState<"branch" | "all">("branch");
  const report = useQuery(() => get("doctorReport", { query: { from, to, ...(scope === "branch" ? { branch_id: branch.id } : {}) } }), [from, to, scope, branch.id]);
  const rows = (report.data?.rows ?? []).slice().sort((a, b) => b.total_cents - a.total_cents);
  const totals = rows.reduce((acc, r) => ({ orders: acc.orders + r.orders_count, studies: acc.studies + r.studies_count, total: acc.total + r.total_cents, paid: acc.paid + r.paid_cents }), { orders: 0, studies: 0, total: 0, paid: 0 });

  return (
    <>
      <PageHead kicker={scope === "branch" ? branch.name : "Todas las sucursales"} title="Reporte por médico" lede="Cuántos pacientes y estudios refirió cada médico en el periodo, y cuánto representaron." />
      <div className="toolbar">
        <Field label="Desde">
          <input {...NO_AUTOFILL} type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Hasta">
          <input {...NO_AUTOFILL} type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label="Alcance">
          <Segmented value={scope} onChange={setScope} options={[{ value: "branch", label: "Esta sucursal" }, { value: "all", label: "Todas" }]} />
        </Field>
      </div>
      <Card pad={false}>
        {report.loading && !report.data ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty icon="stethoscope" title="Sin solicitudes en el periodo" hint={`Del ${fmtDate(from)} al ${fmtDate(to)}.`} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Médico</th>
                  <th className="right">Solicitudes</th>
                  <th className="right">Estudios</th>
                  <th className="right">Facturado</th>
                  <th className="right">Cobrado</th>
                  <th style={{ width: "22%" }}>Participación</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.doctor_id ?? "none"}>
                    <td style={{ fontWeight: 600 }}>{r.doctor_name}</td>
                    <td className="right tnum">{r.orders_count}</td>
                    <td className="right tnum">{r.studies_count}</td>
                    <td className="right">
                      <Money cents={r.total_cents} />
                    </td>
                    <td className="right">
                      <Money cents={r.paid_cents} />
                    </td>
                    <td>
                      <div style={{ height: 8, borderRadius: 4, background: "var(--line-soft)", overflow: "hidden" }}>
                        <div style={{ width: `${totals.total ? (r.total_cents / totals.total) * 100 : 0}%`, height: "100%", background: "var(--brand)" }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 600 }}>Total</td>
                  <td className="right tnum" style={{ fontWeight: 600 }}>
                    {totals.orders}
                  </td>
                  <td className="right tnum" style={{ fontWeight: 600 }}>
                    {totals.studies}
                  </td>
                  <td className="right" style={{ fontWeight: 600 }}>
                    {money(totals.total)}
                  </td>
                  <td className="right" style={{ fontWeight: 600 }}>
                    {money(totals.paid)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
